import React, { forwardRef, Fragment, type ReactNode, type Ref } from 'react';
import { styled } from '@compiled/react';
import { N0 } from '@atlaskit/theme/colors';
import { token } from '@atlaskit/tokens';
import { TransitionState } from '@atlassian/jira-minimizable-modal/src/controllers/use-minimizable-modal/types.tsx';
import { fireScreenAnalyticsDeferred, MountEvent } from '@atlassian/jira-product-analytics-bridge';
import { useMinimizableModal } from '../../../controllers/use-minimizable-modal';
import type { FullscreenContainerProps } from '../../../types';
import ScrollContainer from './scroll-container';

export const FullscreenBody = ({ children, containerStyle }: FullscreenContainerProps) => (
	<ScrollContainer>
		<FullscreenBodyWrapper
			// eslint-disable-next-line jira/react/no-style-attribute, @atlaskit/ui-styling-standard/enforce-style-prop -- Ignored via go/DSP-18766
			style={containerStyle}
		>
			{children}
		</FullscreenBodyWrapper>
	</ScrollContainer>
);

export const Fullscreen = forwardRef<HTMLDivElement, FullscreenContainerProps>(
	({ children, className, components }: FullscreenContainerProps, ref) => {
		const { Header = Fragment, Footer = Fragment } = components || {};

		const { isTransitionInState } = useMinimizableModal();

		const isViewEntered = isTransitionInState(['fullscreen'], TransitionState.ENTERED);

		return (
			<FullscreenWrapper
				ref={ref}
				transitionClass={className}
				data-testid={!isViewEntered ? undefined : 'minimizable-modal.ui.modal-container.fullscreen'}
			>
				<MountEvent onMount={fireScreenAnalyticsDeferred} />
				{components ? (
					<>
						<Header />
						<FullscreenBody>{children}</FullscreenBody>
						<Footer />
					</>
				) : (
					<>{children}</>
				)}
			</FullscreenWrapper>
		);
	},
);

export const FullscreenWrapper = (props: {
	transitionClass?: string;
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	ref: Ref<any>;
	'data-testid'?: string;
	children?: ReactNode;
}) => (
	<FullscreenWrapperExperimentStyles
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Ignored via go/DSP-18766
		className={props.transitionClass}
		{...props}
	/>
);

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const FullscreenBodyWrapper = styled.div({
	padding: token('space.250', '20px'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const FullscreenWrapperExperimentStyles = styled.div({
	width: '100%',
	height: '100%',
	position: 'fixed',
	top: '0px',
	left: '0px',
	background: token('elevation.surface.overlay', N0),
	overflow: 'hidden',
	display: 'flex',
	flexDirection: 'column',
});
